import { useState } from "react";
import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, Menu, X, Loader2 } from "lucide-react";

export default function AdminShell({ brand, subtitle, nav, base, requireRole, storeName }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (user === null) return <div className="vp-dark min-h-screen grid place-items-center text-slate-400"><Loader2 className="animate-spin mr-2" /> Chargement…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (requireRole && !requireRole.includes(user.role)) {
    return (
      <div className="vp-dark min-h-screen grid place-items-center text-center p-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-white mb-2">Accès refusé</h1>
          <p className="text-slate-400 mb-6">Vous n'avez pas les droits pour cet espace.</p>
          <Button onClick={() => navigate("/")} className="vp-accent-bg text-black">Retour à l'accueil</Button>
        </div>
      </div>
    );
  }

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 border-b border-white/10">
        <div className="font-display text-2xl font-bold text-white">{brand}</div>
        {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
      </div>
      <nav className="flex-1 overflow-y-auto no-scrollbar py-4 px-3 space-y-1">
        {nav.map((n) => (
          <NavLink key={n.to} to={`${base}/${n.to}`.replace(/\/+$/, base ? "" : "/")} end={n.end}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                isActive ? "vp-accent-bg text-black font-medium" : "text-slate-300 hover:bg-white/5 hover:text-white"
              }`}
            data-testid={`nav-${n.to || "home"}`}>
            <n.icon size={18} /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10">
        <div className="px-3 py-2 mb-2">
          <div className="text-sm text-white font-medium truncate">{user.name}</div>
          <div className="text-xs text-slate-500 truncate">{user.email}</div>
        </div>
        <Button onClick={() => { logout(); navigate("/login"); }} variant="ghost" className="w-full justify-start text-slate-400 hover:text-white hover:bg-white/5" data-testid="logout-btn">
          <LogOut size={16} className="mr-2" /> Déconnexion
        </Button>
      </div>
    </div>
  );

  return (
    <div className="vp-dark min-h-screen flex">
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-white/10 flex-col fixed inset-y-0">{SidebarContent()}</aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 vp-dark border-r border-white/10">{SidebarContent()}</div>
          <div className="flex-1 bg-black/60" onClick={() => setOpen(false)} />
        </div>
      )}
      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="glass sticky top-0 z-40 h-14 flex items-center justify-between px-4 lg:px-8">
          <button className="lg:hidden text-white" onClick={() => setOpen(true)} data-testid="sidebar-toggle"><Menu /></button>
          <div className="text-sm text-slate-400 hidden lg:block">{storeName || subtitle}</div>
          <div className="lg:hidden font-display text-lg font-bold text-white">{brand}</div>
          <div className="w-8 h-8 rounded-full vp-accent-bg grid place-items-center text-black font-semibold text-sm">
            {(user.name || "?").charAt(0).toUpperCase()}
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
